import { type JSX } from 'react';
import { CodeBlock } from '@gonfalon/code-block/CodeBlock';
import { CodeBlockContainer } from '@gonfalon/code-block/CodeBlockContainer';
import type { SnippetLang } from '@gonfalon/code-block/languages';
import { msg } from '@gonfalon/messages/msg';
import { IconButton } from '@launchpad-ui/components';

import { CopyToClipboard } from '../CopyToClipboard';

import snippetStyles from '@gonfalon/code-block/codeBlock.module.css';

type SnippetProps = {
  children: string | JSX.Element;
  className?: string;
  highlightRange?: string;
  highlightOffset?: number;
  lang: SnippetLang;
  label?: string;
  withHeader?: boolean;
  withLineNumbers?: boolean;
  /** Pass `false` when the snippet sits inside a container that draws its own border. */
  withBorder?: boolean;
  /**
   * How a line too long for the block behaves. Defaults to `'wrap'`; pass `'nowrap'` for code
   * where a line break would be misleading, and the block scrolls sideways instead.
   */
  overflow?: 'wrap' | 'nowrap';
  useDefaultHighlighting?: boolean;
  withCopyButton?: boolean;
  trackAnalyticsOnClick?: () => void;
};

// Example usage:
//
// const json = JSON.stringify({
//    'key': 'test@test.com',
//    'ip': '192.168.0.1',
//    'custom': {
//      'customer_ranking': 10004
//    }
// }, null, 2);
//
// <Snippet withCopyButton={true} lang="json">{json}</Snippet>
export function Snippet({
  children,
  className,
  highlightRange,
  highlightOffset,
  lang,
  label,
  withHeader,
  withLineNumbers,
  withBorder = true,
  overflow,
  useDefaultHighlighting = false,
  withCopyButton,
  trackAnalyticsOnClick,
}: SnippetProps) {
  return (
    <>
      {withHeader && (
        <div className={snippetStyles.header}>
          {label && <span>{label}</span>}
          {lang && <span>{lang}</span>}
        </div>
      )}
      <CodeBlockContainer
        className={className}
        withBorder={withBorder}
        overflow={overflow}
        copyable={withCopyButton}
        useDefaultHighlighting={useDefaultHighlighting}
      >
        <CodeBlock
          lang={lang}
          withLineNumbers={withLineNumbers}
          highlightRange={highlightRange}
          highlightOffset={highlightOffset}
          action={
            withCopyButton && (
              <CopyToClipboard text={children as string} showTooltip={false}>
                <IconButton
                  className={snippetStyles.copyCodeSnippet}
                  aria-label={msg('ui.snippet.copyLabel')}
                  variant="minimal"
                  icon="copy-code"
                  onPress={trackAnalyticsOnClick}
                />
              </CopyToClipboard>
            )
          }
        >
          {children}
        </CodeBlock>
      </CodeBlockContainer>
    </>
  );
}
